import { useState, useMemo, useCallback, useEffect } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Calendar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@pantry/ui';
import { cn } from '@pantry/ui';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ArchiveBoxIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  MinusIcon,
  CheckIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  useItems,
  useStorageLocations,
  useItemInstances,
  type ItemWithInstanceCounts,
  type ItemInstance,
  type StorageLocation,
  type ItemUnit,
  UNIT_DISPLAY_LABELS,
  getQuantityStepSize,
  getQuickSelectOptions,
  formatQuantityDisplay,
  getUnitCategory,
  convertQuantity,
} from '@pantry/api-client';
import { useMealPrepStore, type PendingMealPrepItem } from '@pantry/shared-react/stores';
import { UnitSelector } from '../items/unit-selector';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import {
  type Product,
  EXPIRY_PRESETS,
  calculateExpiryDate,
  parseLocalDate,
  getDaysUntilExpiry,
  formatExpiryDate,
} from '@pantry/shared';
import { toast } from 'sonner';

interface InventoryPickerSheetProps {
  /** Whether the sheet is open */
  isOpen: boolean;
  /** Callback when sheet is closed */
  onClose: () => void;
  /** Household ID to fetch items for */
  householdId: string;
  /**
   * Callback when an item is selected.
   * Passes the item (with instance counts) and all its active instances.
   * If multiple instances exist, parent should show InstancePickerSheet.
   */
  onSelectItem: (item: ItemWithInstanceCounts, instances: ItemInstance[]) => void;
}

/**
 * Simple debounce hook for search input.
 */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Get icon for storage location type.
 */
function getLocationIcon(type: string) {
  switch (type) {
    case 'fridge':
      return '🧊';
    case 'freezer':
      return '❄️';
    case 'pantry':
      return '🥫';
    default:
      return '📦';
  }
}

/**
 * Get expiry status and styling.
 */
function getExpiryStatus(
  expiryDate: string | null,
  t?: ReturnType<typeof useLingui>['t'],
  locale?: string
): {
  status: 'expired' | 'expiring-soon' | 'fresh' | 'none';
  label: string | null;
} {
  if (!expiryDate) return { status: 'none', label: null };

  const diffDays = getDaysUntilExpiry(expiryDate);
  const formattedDate = formatExpiryDate(expiryDate, locale);

  if (diffDays < 0) {
    return {
      status: 'expired',
      label: t ? t`Expired ${formattedDate}` : `Expired ${formattedDate}`,
    };
  }
  if (diffDays === 0) {
    return { status: 'expiring-soon', label: t ? t`Expires today` : 'Expires today' };
  }
  if (diffDays <= 3) {
    return {
      status: 'expiring-soon',
      label: t ? t`Expires ${formattedDate}` : `Expires ${formattedDate}`,
    };
  }
  return { status: 'fresh', label: formattedDate };
}

/**
 * Slide-up sheet for searching and selecting inventory items.
 * Items are grouped by storage location and filtered by search.
 * Shows both in-stock items (clickable) and out-of-stock items (with Add & Use).
 */
export function InventoryPickerSheet({
  isOpen,
  onClose,
  householdId,
  onSelectItem,
}: InventoryPickerSheetProps) {
  const { t } = useLingui();
  const [searchInput, setSearchInput] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Track which out-of-stock items are expanded for configuration
  const [expandedOutOfStock, setExpandedOutOfStock] = useState<Set<string>>(new Set());
  const resolveProductName = useResolveProductName();

  // Get store methods and state
  const {
    getSelectionCount,
    getPendingItemsCount,
    addPendingItem,
    updatePendingItem,
    removePendingItem,
    getPendingItems,
  } = useMealPrepStore();

  const selectionCount = getSelectionCount();
  const pendingItemsCount = getPendingItemsCount();
  const totalCount = selectionCount + pendingItemsCount;
  const pendingItems = getPendingItems();

  // Debounce search input
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Fetch items with search filter
  const {
    data: itemsData,
    isLoading: itemsLoading,
    isFetching,
  } = useItems(householdId, {
    search: debouncedSearch || undefined,
    hideDepleted: true,
  });
  const items = itemsData as ItemWithInstanceCounts[] | undefined;

  // Fetch storage locations
  const { data: storageLocations } = useStorageLocations(householdId);

  // Expand all groups by default when locations load
  useEffect(() => {
    if (storageLocations && expandedGroups.size === 0) {
      setExpandedGroups(
        new Set(['unassigned', 'out-of-stock', ...storageLocations.map((l) => l.id)])
      );
    }
  }, [storageLocations, expandedGroups.size]);

  // Reset state when sheet opens
  useEffect(() => {
    if (isOpen) {
      setSearchInput('');
      setExpandedOutOfStock(new Set());
    }
  }, [isOpen]);

  // Split items into in-stock and out-of-stock
  const { inStockItems, outOfStockItems } = useMemo(() => {
    if (!items) return { inStockItems: [], outOfStockItems: [] };
    const inStock: ItemWithInstanceCounts[] = [];
    const outOfStock: ItemWithInstanceCounts[] = [];
    for (const item of items) {
      if ((item.activeQuantity ?? 0) > 0) {
        inStock.push(item);
      } else {
        outOfStock.push(item);
      }
    }
    return { inStockItems: inStock, outOfStockItems: outOfStock };
  }, [items]);

  // Group in-stock items by storage location
  const groupedItems = useMemo(() => {
    const groups = new Map<string | null, ItemWithInstanceCounts[]>();

    for (const item of inStockItems) {
      const locationId = item.storageLocationId;
      const existing = groups.get(locationId) || [];
      groups.set(locationId, [...existing, item]);
    }

    return groups;
  }, [inStockItems]);

  // Sort storage locations
  const sortedLocations = useMemo(() => {
    if (!storageLocations) return [];
    return [...storageLocations].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [storageLocations]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    setSearchInput('');
    setExpandedOutOfStock(new Set());
    onClose();
  }, [onClose]);

  // Handle adding out-of-stock item - expand for configuration
  const handleAddOutOfStock = useCallback(
    (item: ItemWithInstanceCounts) => {
      const itemName =
        item.customName ||
        (item.product ? resolveProductName(item.product as Product) : null) ||
        t`Unnamed Item`;
      const clientId = addPendingItem({
        productId: item.product?.id,
        productName: itemName,
        productBrand: item.product?.brand || undefined,
        productImageUrl: item.product?.imageUrl || undefined,
        quantity: item.product?.defaultQuantity || 1,
        unit: item.product?.defaultUnit || 'item',
      });
      setExpandedOutOfStock((prev) => new Set(prev).add(item.id));
      toast.success(t`${itemName} added - configure below`);
    },
    [addPendingItem, resolveProductName, t]
  );

  // Check if an out-of-stock item has been added as pending
  const getPendingItemForProduct = useCallback(
    (productId: string | undefined): PendingMealPrepItem | null => {
      if (!productId) return null;
      return pendingItems.find((p) => p.productId === productId) ?? null;
    },
    [pendingItems]
  );

  // Unassigned items (no storage location)
  const unassignedItems = groupedItems.get(null) || [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-2xl shadow-xl animate-in slide-in-from-bottom duration-300 max-h-[85vh] flex flex-col">
        <Card className="border-0 shadow-none flex flex-col flex-1 min-h-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 flex-shrink-0">
            <div>
              <CardTitle className="text-lg">
                <Trans>Pick from Inventory</Trans>
              </CardTitle>
              {totalCount > 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  <Trans>
                    <Plural value={totalCount} one="# item" other="# items" /> selected
                  </Trans>
                  {pendingItemsCount > 0 && <> ({t`${pendingItemsCount} new`})</>}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {totalCount > 0 && (
                <Button variant="default" size="sm" onClick={handleClose}>
                  <Trans>Done</Trans>
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleClose}>
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="flex flex-col flex-1 min-h-0 pb-8 space-y-4">
            {/* Search input */}
            <div className="relative flex-shrink-0">
              <Label htmlFor="inventory-search" className="sr-only">
                <Trans>Search items</Trans>
              </Label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="inventory-search"
                  type="text"
                  placeholder={t`Search items...`}
                  className="pl-10"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  autoFocus
                />
                {isFetching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                )}
              </div>
            </div>

            {/* Loading state */}
            {itemsLoading && (!items || items.length === 0) && (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            )}

            {/* Empty state */}
            {!itemsLoading && (!items || items.length === 0) && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ArchiveBoxIcon className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {debouncedSearch ? (
                    <Trans>No items found</Trans>
                  ) : (
                    <Trans>No items in inventory</Trans>
                  )}
                </h3>
                <p className="text-muted-foreground max-w-[280px]">
                  {debouncedSearch ? (
                    <Trans>Try adjusting your search term.</Trans>
                  ) : (
                    <Trans>Add items to your inventory first.</Trans>
                  )}
                </p>
              </div>
            )}

            {/* Items list */}
            {(items?.length ?? 0) > 0 && (
              <div className="flex-1 overflow-y-auto space-y-3">
                {/* In-stock items grouped by location */}
                {sortedLocations.map((location) => {
                  const locationItems = groupedItems.get(location.id) || [];
                  if (locationItems.length === 0) return null;

                  const isExpanded = expandedGroups.has(location.id);

                  return (
                    <LocationGroup
                      key={location.id}
                      title={location.name}
                      icon={getLocationIcon(location.type)}
                      itemCount={locationItems.length}
                      isExpanded={isExpanded}
                      onToggle={() => toggleGroup(location.id)}
                    >
                      <div className="space-y-2">
                        {locationItems.map((item) => (
                          <InventoryItemRow
                            key={item.id}
                            item={item}
                            householdId={householdId}
                            onSelect={onSelectItem}
                          />
                        ))}
                      </div>
                    </LocationGroup>
                  );
                })}

                {/* Unassigned in-stock items */}
                {unassignedItems.length > 0 && (
                  <LocationGroup
                    title={t`Unassigned`}
                    icon="📍"
                    itemCount={unassignedItems.length}
                    isExpanded={expandedGroups.has('unassigned')}
                    onToggle={() => toggleGroup('unassigned')}
                    muted
                  >
                    <div className="space-y-2">
                      {unassignedItems.map((item) => (
                        <InventoryItemRow
                          key={item.id}
                          item={item}
                          householdId={householdId}
                          onSelect={onSelectItem}
                        />
                      ))}
                    </div>
                  </LocationGroup>
                )}

                {/* Out of stock items */}
                {outOfStockItems.length > 0 && (
                  <LocationGroup
                    title={t`Out of Stock`}
                    icon="📭"
                    itemCount={outOfStockItems.length}
                    isExpanded={expandedGroups.has('out-of-stock')}
                    onToggle={() => toggleGroup('out-of-stock')}
                    muted
                  >
                    <div className="space-y-2">
                      {outOfStockItems.map((item) => {
                        const pendingItem = getPendingItemForProduct(item.product?.id);
                        return (
                          <OutOfStockItemRow
                            key={item.id}
                            item={item}
                            householdId={householdId}
                            pendingItem={pendingItem}
                            isExpanded={expandedOutOfStock.has(item.id) || !!pendingItem}
                            onAddAndUse={() => handleAddOutOfStock(item)}
                            onUpdatePending={updatePendingItem}
                            onRemovePending={removePendingItem}
                            storageLocations={storageLocations || []}
                          />
                        );
                      })}
                    </div>
                  </LocationGroup>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * Collapsible group for items in a storage location.
 */
function LocationGroup({
  title,
  icon,
  itemCount,
  isExpanded,
  onToggle,
  muted = false,
  children,
}: {
  title: string;
  icon: string;
  itemCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  muted?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('rounded-lg border', muted && 'border-dashed')}>
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between p-3 text-left hover:bg-muted/50 transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <div>
            <h3 className={cn('font-medium text-sm', muted && 'text-muted-foreground')}>{title}</h3>
            <p className="text-xs text-muted-foreground">
              <Plural value={itemCount} one="# item" other="# items" />
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {isExpanded && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

/**
 * Single inventory item row - clickable to select.
 */
function InventoryItemRow({
  item,
  householdId,
  onSelect,
}: {
  item: ItemWithInstanceCounts;
  householdId: string;
  onSelect: (item: ItemWithInstanceCounts, instances: ItemInstance[]) => void;
}) {
  const { t, i18n } = useLingui();
  // Fetch instances for this item
  const { data: instances } = useItemInstances(householdId, item.id);
  const { isSelected } = useMealPrepStore();
  const resolveProductName = useResolveProductName();

  // Filter to active instances only
  const activeInstances = useMemo(() => {
    if (!instances) return [];
    return instances
      .filter((i) => i.status === 'active')
      .sort((a, b) => {
        // Sort by expiry date (earliest first, null dates last)
        if (!a.expiryDate && !b.expiryDate) return 0;
        if (!a.expiryDate) return 1;
        if (!b.expiryDate) return -1;
        return a.expiryDate.localeCompare(b.expiryDate);
      });
  }, [instances]);

  // Check if any instance of this item is selected
  const hasSelectedInstance = activeInstances.some((i) => isSelected(i.id));

  // Get earliest expiry for display
  const earliestExpiry = activeInstances.find((i) => i.expiryDate)?.expiryDate ?? null;
  const expiryInfo = getExpiryStatus(earliestExpiry, t, i18n.locale);

  // Display name
  const itemName =
    item.customName ||
    (item.product ? resolveProductName(item.product as Product) : null) ||
    t`Unnamed Item`;
  const itemBrand = item.product?.brand;
  const itemImage = item.product?.imageUrl;

  const handleClick = () => {
    if (activeInstances.length > 0) {
      onSelect(item, activeInstances);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={activeInstances.length === 0}
      className={cn(
        'flex w-full items-center gap-3 p-3 rounded-lg text-left transition-colors',
        'hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        hasSelectedInstance && 'bg-primary/10 border border-primary/30',
        activeInstances.length === 0 && 'opacity-50 cursor-not-allowed'
      )}
    >
      {/* Selection indicator */}
      <div
        className={cn(
          'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all',
          hasSelectedInstance ? 'bg-primary border-primary' : 'border-muted-foreground/30'
        )}
      >
        {hasSelectedInstance && <CheckIcon className="h-3 w-3 text-primary-foreground" />}
      </div>

      {/* Product image */}
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg overflow-hidden bg-muted">
        {itemImage ? (
          <img src={itemImage} alt={itemName} className="h-full w-full object-cover" />
        ) : (
          <ArchiveBoxIcon className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      {/* Item info */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm truncate">{itemName}</h4>
        {itemBrand && <p className="text-xs text-muted-foreground truncate">{itemBrand}</p>}
        <div className="flex flex-wrap items-center gap-2 mt-1">
          {/* Quantity available */}
          <span className="text-xs text-muted-foreground">
            {item.activeQuantity ?? 0} {UNIT_DISPLAY_LABELS[item.unit]}
          </span>

          {/* Instance count if multiple */}
          {activeInstances.length > 1 && (
            <span className="inline-flex items-center rounded-full bg-primary/20 px-1.5 py-0.5 text-xs text-primary">
              <Trans>{activeInstances.length} entries</Trans>
            </span>
          )}

          {/* Expiry date */}
          {expiryInfo.label && (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-1.5 py-0.5 text-xs',
                expiryInfo.status === 'expired' && 'bg-expired/20 text-expired',
                expiryInfo.status === 'expiring-soon' && 'bg-expiring-soon/20 text-expiring-soon',
                expiryInfo.status === 'fresh' && 'bg-muted text-muted-foreground'
              )}
            >
              {expiryInfo.label}
            </span>
          )}
        </div>
      </div>

      {/* Arrow indicator */}
      <ChevronRightIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
    </button>
  );
}

/**
 * Out-of-stock item row with Add & Use functionality and inline configuration.
 */
function OutOfStockItemRow({
  item,
  householdId,
  pendingItem,
  isExpanded,
  onAddAndUse,
  onUpdatePending,
  onRemovePending,
  storageLocations,
}: {
  item: ItemWithInstanceCounts;
  householdId: string;
  pendingItem: PendingMealPrepItem | null;
  isExpanded: boolean;
  onAddAndUse: () => void;
  onUpdatePending: (clientId: string, updates: Partial<PendingMealPrepItem>) => void;
  onRemovePending: (clientId: string) => void;
  storageLocations: StorageLocation[];
}) {
  const { t } = useLingui();
  const resolveProductName = useResolveProductName();
  const itemName =
    item.customName ||
    (item.product ? resolveProductName(item.product as Product) : null) ||
    t`Unnamed Item`;
  const itemBrand = item.product?.brand;
  const itemImage = item.product?.imageUrl;

  // If not added yet, show simple row with Add & Use button
  if (!pendingItem) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg opacity-60 hover:opacity-100 transition-opacity">
        {/* Product image */}
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg overflow-hidden bg-muted/50">
          {itemImage ? (
            <img src={itemImage} alt={itemName} className="h-full w-full object-cover" />
          ) : (
            <ArchiveBoxIcon className="h-5 w-5 text-muted-foreground" />
          )}
        </div>

        {/* Item info */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm truncate text-muted-foreground">{itemName}</h4>
          {itemBrand && <p className="text-xs text-muted-foreground truncate">{itemBrand}</p>}
          <p className="text-xs text-muted-foreground">
            <Trans>No active stock</Trans>
          </p>
        </div>

        {/* Add & Use button */}
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          onClick={onAddAndUse}
        >
          <PlusIcon className="h-3.5 w-3.5 mr-1" />
          <Trans>Add & Use</Trans>
        </Button>
      </div>
    );
  }

  // Item has been added - show configuration UI
  return (
    <InlinePendingItemConfig
      pendingItem={pendingItem}
      itemImage={itemImage}
      onUpdate={(updates) => onUpdatePending(pendingItem.clientId, updates)}
      onRemove={() => onRemovePending(pendingItem.clientId)}
      storageLocations={storageLocations}
    />
  );
}

/**
 * Inline configuration for a pending item within the picker sheet.
 */
function InlinePendingItemConfig({
  pendingItem,
  itemImage,
  onUpdate,
  onRemove,
  storageLocations,
}: {
  pendingItem: PendingMealPrepItem;
  itemImage: string | null | undefined;
  onUpdate: (updates: Partial<PendingMealPrepItem>) => void;
  onRemove: () => void;
  storageLocations: StorageLocation[];
}) {
  const { t, i18n } = useLingui();
  // Track display unit for unit switching (may differ from stored unit)
  const [displayUnit, setDisplayUnit] = useState<ItemUnit>(pendingItem.unit);
  const [displayQuantity, setDisplayQuantity] = useState(pendingItem.quantity);
  const [displayQuantityToUse, setDisplayQuantityToUse] = useState(
    pendingItem.quantityToUse ?? pendingItem.quantity
  );

  const displayName = pendingItem.customName || pendingItem.productName || t`Unknown Item`;
  const canSwitchUnits = getUnitCategory(pendingItem.unit) !== 'discrete';
  const stepSize = getQuantityStepSize(displayUnit);
  const quickOptionsHave = getQuickSelectOptions(displayUnit, 10000);
  const quickOptionsUse = getQuickSelectOptions(displayUnit, displayQuantity);

  const selectedLocation = storageLocations.find((loc) => loc.id === pendingItem.storageLocationId);
  const isValid =
    !!(pendingItem.productId || pendingItem.customName) && !!pendingItem.storageLocationId;

  // Convert display quantity to stored unit and update
  const updateStoredQuantity = useCallback(
    (newDisplayQty: number) => {
      setDisplayQuantity(newDisplayQty);
      let storedQty = newDisplayQty;
      if (displayUnit !== pendingItem.unit && canSwitchUnits) {
        const converted = convertQuantity(newDisplayQty, displayUnit, pendingItem.unit);
        if (converted !== null) {
          storedQty = Math.round(converted * 1000) / 1000;
        }
      }
      const newDisplayToUse = Math.min(displayQuantityToUse, newDisplayQty);
      setDisplayQuantityToUse(newDisplayToUse);
      let storedToUse = newDisplayToUse;
      if (displayUnit !== pendingItem.unit && canSwitchUnits) {
        const converted = convertQuantity(newDisplayToUse, displayUnit, pendingItem.unit);
        if (converted !== null) {
          storedToUse = Math.round(converted * 1000) / 1000;
        }
      }
      onUpdate({ quantity: storedQty, quantityToUse: storedToUse });
    },
    [displayUnit, pendingItem.unit, canSwitchUnits, displayQuantityToUse, onUpdate]
  );

  const updateStoredQuantityToUse = useCallback(
    (newDisplayToUse: number) => {
      setDisplayQuantityToUse(newDisplayToUse);
      let storedToUse = newDisplayToUse;
      if (displayUnit !== pendingItem.unit && canSwitchUnits) {
        const converted = convertQuantity(newDisplayToUse, displayUnit, pendingItem.unit);
        if (converted !== null) {
          storedToUse = Math.round(converted * 1000) / 1000;
        }
      }
      onUpdate({ quantityToUse: storedToUse });
    },
    [displayUnit, pendingItem.unit, canSwitchUnits, onUpdate]
  );

  const handleQuantityChange = (delta: number) => {
    const newQty = Math.max(stepSize, displayQuantity + delta * stepSize);
    const rounded = Math.round(newQty * 1000) / 1000;
    updateStoredQuantity(rounded);
  };

  const handleQuantityToUseChange = (delta: number) => {
    const newToUse = Math.max(
      stepSize,
      Math.min(displayQuantity, displayQuantityToUse + delta * stepSize)
    );
    const rounded = Math.round(newToUse * 1000) / 1000;
    updateStoredQuantityToUse(rounded);
  };

  const handleDisplayUnitChange = useCallback(
    (newUnit: ItemUnit) => {
      if (newUnit === displayUnit) return;
      const convertedQty = convertQuantity(displayQuantity, displayUnit, newUnit);
      const convertedToUse = convertQuantity(displayQuantityToUse, displayUnit, newUnit);
      if (convertedQty !== null) {
        setDisplayQuantity(Math.round(convertedQty * 1000) / 1000);
      }
      if (convertedToUse !== null) {
        setDisplayQuantityToUse(Math.round(convertedToUse * 1000) / 1000);
      }
      setDisplayUnit(newUnit);
    },
    [displayUnit, displayQuantity, displayQuantityToUse]
  );

  const handleExpiryPreset = (days: number) => {
    const targetDate = calculateExpiryDate(days);
    if (pendingItem.expiryDate === targetDate) {
      onUpdate({ expiryDate: undefined });
    } else {
      onUpdate({ expiryDate: targetDate });
    }
  };

  return (
    <div
      className={cn(
        'rounded-lg border-2 border-dashed p-3 space-y-3 transition-all',
        isValid ? 'border-primary/50 bg-primary/5' : 'border-yellow-500 bg-yellow-50/30'
      )}
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        {/* Product image */}
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg overflow-hidden bg-muted">
          {itemImage ? (
            <img src={itemImage} alt={displayName} className="h-full w-full object-cover" />
          ) : (
            <ArchiveBoxIcon className="h-5 w-5 text-muted-foreground" />
          )}
        </div>

        {/* Name and brand */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center text-[10px] font-medium text-primary bg-primary/20 px-1.5 py-0.5 rounded-full">
              <Trans>NEW</Trans>
            </span>
          </div>
          <h4 className="font-medium text-sm truncate">{displayName}</h4>
          {pendingItem.productBrand && (
            <p className="text-xs text-muted-foreground truncate">{pendingItem.productBrand}</p>
          )}
        </div>

        {/* Remove button */}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
          onClick={onRemove}
        >
          <XMarkIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Quantity controls - compact version */}
      <div className="space-y-2">
        {/* Have row */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-10">
            <Trans>Have:</Trans>
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => handleQuantityChange(-1)}
              disabled={displayQuantity <= stepSize}
            >
              <MinusIcon className="h-3 w-3" />
            </Button>
            <span className="text-xs font-medium min-w-[50px] text-center">
              {formatQuantityDisplay(displayQuantity, displayUnit)}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => handleQuantityChange(1)}
            >
              <PlusIcon className="h-3 w-3" />
            </Button>
            {canSwitchUnits && (
              <UnitSelector
                value={displayUnit}
                onChange={handleDisplayUnitChange}
                baseUnit={pendingItem.unit}
                className="h-6 text-xs px-1"
              />
            )}
          </div>
          <div className="flex gap-1 flex-wrap">
            {quickOptionsHave.slice(0, 4).map((option) => (
              <Button
                key={option.label}
                variant="outline"
                size="sm"
                className={cn(
                  'h-5 px-1.5 text-[10px]',
                  Math.abs(displayQuantity - option.value) < 0.001 && 'border-primary bg-primary/10'
                )}
                onClick={() => updateStoredQuantity(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Using row */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-10">
            <Trans>Using:</Trans>
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => handleQuantityToUseChange(-1)}
              disabled={displayQuantityToUse <= stepSize}
            >
              <MinusIcon className="h-3 w-3" />
            </Button>
            <span className="text-xs font-medium min-w-[50px] text-center">
              {formatQuantityDisplay(displayQuantityToUse, displayUnit)}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => handleQuantityToUseChange(1)}
              disabled={displayQuantityToUse >= displayQuantity}
            >
              <PlusIcon className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex gap-1 flex-wrap">
            {quickOptionsUse.slice(0, 4).map((option) => (
              <Button
                key={option.label}
                variant="outline"
                size="sm"
                className={cn(
                  'h-5 px-1.5 text-[10px]',
                  Math.abs(displayQuantityToUse - option.value) < 0.001 &&
                    'border-fresh bg-fresh/10'
                )}
                onClick={() => updateStoredQuantityToUse(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Storage location */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground w-10">
          <Trans>Store:</Trans>
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'h-6 gap-1 flex-1 justify-between text-xs',
                !pendingItem.storageLocationId && 'border-yellow-500 text-yellow-700'
              )}
            >
              <span className="truncate">{selectedLocation?.name || t`Select location`}</span>
              <ChevronDownIcon className="h-3 w-3 flex-shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            {storageLocations.map((location) => (
              <DropdownMenuItem
                key={location.id}
                onClick={() => onUpdate({ storageLocationId: location.id })}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <span>{getLocationIcon(location.type)}</span>
                  <span>{location.name}</span>
                </div>
                {pendingItem.storageLocationId === location.id && <CheckIcon className="h-4 w-4" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Expiry date */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground w-10">
          <Trans>Expiry:</Trans>
        </span>
        <div className="flex gap-1 flex-wrap items-center">
          {EXPIRY_PRESETS.map(({ label, days }) => {
            const targetDate = calculateExpiryDate(days);
            const isSelected = pendingItem.expiryDate === targetDate;
            return (
              <Button
                key={label}
                variant={isSelected ? 'default' : 'outline'}
                size="sm"
                className="h-5 px-1.5 text-[10px]"
                onClick={() => handleExpiryPreset(days)}
              >
                {label}
              </Button>
            );
          })}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={
                  pendingItem.expiryDate &&
                  !EXPIRY_PRESETS.some(
                    ({ days }) => calculateExpiryDate(days) === pendingItem.expiryDate
                  )
                    ? 'default'
                    : 'outline'
                }
                size="sm"
                className="h-5 px-1.5 gap-1"
              >
                <CalendarIcon className="h-3 w-3" />
                {pendingItem.expiryDate &&
                !EXPIRY_PRESETS.some(
                  ({ days }) => calculateExpiryDate(days) === pendingItem.expiryDate
                ) ? (
                  <span className="text-[10px]">
                    {formatExpiryDate(pendingItem.expiryDate, i18n.locale)}
                  </span>
                ) : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={pendingItem.expiryDate ? new Date(pendingItem.expiryDate) : undefined}
                onSelect={(date) => {
                  if (date) {
                    onUpdate({ expiryDate: date.toISOString().split('T')[0] });
                  }
                }}
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Validation warning */}
      {!isValid && (
        <div className="flex items-center gap-1.5 text-[10px] text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
          <ExclamationTriangleIcon className="h-3 w-3 flex-shrink-0" />
          <span>
            <Trans>Select a storage location</Trans>
          </span>
        </div>
      )}
    </div>
  );
}
